import { CalendarClock, PackageCheck } from "lucide-react";
import { useState } from "react";

import type { LtbDecision, Obligation, UC5Job } from "../../lib/api";
import { roleBlock, type Me } from "../../lib/me";
import { serverTime } from "../../lib/when";
import {
  createBody, FOLLOW_ON, lifeOfNeedOptions, LTB_INPUTS, ltbActions, ltbStep,
  type LtbForm, type LtbRequest,
} from "../../pages/vendorFlow";
import { Explainer } from "../Explainer";
import { OUTCOME_LABELS } from "../MarketResults";
import { RoleButton, RoleGroup } from "../RequireRole";
import { TableScroll } from "../TableScroll";
import { SourceLink } from "../SourceLink";

const STATE_WORDS: Record<string, { label: string; tone: string }> = {
  proposed: { label: "proposed", tone: "review" },
  approved: { label: "approved, not yet bought", tone: "review" },
  bridged: { label: "bought, follow-on needed", tone: "info" },
  resolved: { label: "resolved", tone: "ok" },
  closed: { label: "closed", tone: "ok" },
};

const INPUT_LABEL = Object.fromEntries(LTB_INPUTS.map((i) => [i.name, i.label.toLowerCase()]));

const outcome = (o: string) => OUTCOME_LABELS[o] ?? o.replace(/_/g, " ");

function pastDue(due: string, now: Date): boolean {
  const at = serverTime(due);
  return !!at && at.getTime() < now.getTime();
}

export function Obligations({ items, now }: { items: Obligation[]; now: Date }) {
  if (!items.length) return null;
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0" }}>
      {items.map((o, i) => (
        <li key={`${o.kind}-${i}`} className="row" style={{ gap: 8, flexWrap: "wrap", fontSize: 14 }}>
          <CalendarClock size={16} aria-hidden="true" />
          <span>Due <span className="mono">{o.due}</span>: {o.description}</span>
          {o.open
            ? pastDue(o.due, now) ? <span className="pill fail">past due</span> : <span className="pill review">open</span>
            : <span className="pill ok">done</span>}
        </li>
      ))}
    </ul>
  );
}

function emptyForm(): LtbForm {
  return {
    remaining_years: { value: "", source: "" }, rate: { value: "", source: "" },
    stock: { value: "", source: "" }, moq: { value: "", source: "" },
  };
}

/** The form, filled with the figures and sources a proposal was worked out from. */
export function formFrom(d: LtbDecision): LtbForm {
  const form = emptyForm();
  for (const i of d.recommendation.inputs) {
    if (i.name in form) {
      form[i.name as keyof LtbForm] = { value: i.value === null ? "" : String(i.value), source: i.source ?? "" };
    }
  }
  return form;
}

function Recommendation({ d }: { d: LtbDecision }) {
  const r = d.recommendation;
  return (
    <div style={{ marginTop: 12 }}>
      {r.status === "cannot_determine" ? (
        <p style={{ fontSize: 14 }}>
          <span className="pill fail">quantity cannot be determined</span>{" "}
          Missing: {r.missing.map((m) => INPUT_LABEL[m] ?? m).join(", ")}.
        </p>
      ) : (
        <p style={{ fontSize: 14 }}>
          <strong>Buy {r.quantity}</strong> <span className="muted">({r.explanation})</span>
        </p>
      )}
      <TableScroll label="Inputs to the quantity" style={{ marginTop: 8 }}>
      <table>
        <thead><tr><th>Input</th><th>Value</th><th>Source</th></tr></thead>
        <tbody>
          {r.inputs.map((i) => (
            <tr key={i.name}>
              <td>{INPUT_LABEL[i.name] ?? i.name}</td>
              <td className="mono">{i.value ?? <span className="muted">missing</span>}</td>
              <td className="muted" style={{ fontSize: 12 }}>{i.source ?? "no source given"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </TableScroll>
    </div>
  );
}

/**
 * The last-time-buy decision on a life-of-need-buy option: propose with sourced inputs, approve,
 * record the buy, then a follow-on resolution or a reasoned close. A bridge buy postpones the
 * real fix, so a bought case stays open. Renders nothing when there is nothing to decide.
 */
export function LtbView({ job, me, busy, error, onStep, now = new Date() }: {
  job: UC5Job; me: Me | null | undefined; busy: boolean; error: string | null;
  onStep: (body: LtbRequest) => void; now?: Date;
}) {
  const d = job.ltb_decision ?? null;
  const options = lifeOfNeedOptions(job);
  const actions = ltbActions(d);
  const [form, setForm] = useState<LtbForm>(emptyForm);
  const [pick, setPick] = useState<number | null>(null);
  const [quantity, setQuantity] = useState("");
  const [resolution, setResolution] = useState<string>(FOLLOW_ON[0]);
  const [reason, setReason] = useState("");
  // Which proposal the form was opened to change. A new proposal (or any step) is a different
  // decision, so the form folds away again on its own once the quantity has been worked out.
  const decisionKey = d ? JSON.stringify([d.state, d.recommendation, d.option]) : null;
  const [changing, setChanging] = useState<string | null>(null);
  if (!d && !options.length) return null;

  const optionIndex = pick ?? options[0];
  const chosen = optionIndex === undefined ? null : job.candidates?.[optionIndex];
  const cannotApprove = d?.recommendation.status === "cannot_determine";
  const words = d ? STATE_WORDS[d.state] ?? { label: d.state, tone: "info" } : null;
  const canCreate = actions.includes("create") && options.length > 0;
  // The group below disables everything for anyone below an engineer; each button says so too.
  const locked = !!roleBlock(me, "engineer");
  /** A proposal is on screen: its inputs stay folded until a person asks to change them. */
  const folded = d?.state === "proposed" && changing !== decisionKey;
  function workOut() {
    if (optionIndex === undefined) return;
    const body = createBody(optionIndex, form);
    // Unchanged inputs are the proposal already shown: fold the form rather than ask again.
    if (d?.state === "proposed" && chosen && JSON.stringify(chosen) === JSON.stringify(d.option)
        && JSON.stringify(createBody(optionIndex, formFrom(d))) === JSON.stringify(body)) {
      setChanging(null);
      return;
    }
    onStep(body);
  }
  function changeInputs() {
    if (!d) return;
    setForm(formFrom(d));
    const at = job.candidates?.findIndex((c) => JSON.stringify(c) === JSON.stringify(d.option)) ?? -1;
    if (at >= 0) setPick(at);
    setChanging(decisionKey);
  }

  return (
    <div className="card">
      <div className="section-title">
        <span className="stage-chip">Decision</span><h3 className="row"><PackageCheck size={18} /> Last-time buy</h3>
      </div>
      <Explainer>
        A last-time buy stocks enough of this part for the asset's remaining life before the
        manufacturer stops taking orders. Every input needs a figure and where it came from; a
        missing one leaves the quantity undetermined. Buying bridges the gap, so the case stays open
        until a substitute or redesign is chosen or someone closes it with a reason. Nothing here
        places an order.
      </Explainer>

      {error && <p style={{ marginTop: 12 }} role="alert"><span className="pill fail">error</span> {error}</p>}

      {d && words && (
        <div style={{ marginTop: 14 }}>
          <p className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className={`pill ${words.tone}`}>{words.label}</span>
            <span style={{ fontSize: 14 }}>
              on <span className="mono">{d.option.part_number ?? d.option.description ?? "the option"}</span>
              {d.deadline ? <>, orders close <span className="mono">{d.deadline}</span></> : null}
            </span>
          </p>
          <Obligations items={job.obligations ?? []} now={now} />
          <Recommendation d={d} />
          <ul className="muted" style={{ fontSize: 13, marginTop: 10, paddingLeft: 18 }}>
            <li>Proposed by {d.proposed_by}</li>
            {d.approved_by && <li>Approved by {d.approved_by}</li>}
            {d.bought && <li>Bought {d.bought.quantity ?? "an unrecorded quantity"}, recorded by {d.bought.by}</li>}
            {d.resolved && <li>Resolved as {outcome(d.resolved.resolution)} by {d.resolved.by}</li>}
            {d.closed && <li>Closed by {d.closed.by}: {d.closed.reason}</li>}
            {d.history?.length ? <li>{d.history.length} earlier decision{d.history.length === 1 ? "" : "s"} kept</li> : null}
          </ul>
        </div>
      )}

      <RoleGroup role="engineer" me={me} label="Last-time buy decision">
      {actions.includes("approve") && (
        <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <button disabled={locked || busy || cannotApprove}
                      title={cannotApprove ? "Supply the missing inputs and propose again" : undefined}
                      onClick={() => onStep({ action: "approve" })}>Approve the buy</button>
          {cannotApprove && (
            <span className="muted" style={{ fontSize: 12 }}>Change the inputs to supply what is missing, then work out the quantity again.</span>
          )}
        </div>
      )}

      {actions.includes("record_buy") && (
        <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <label className="muted" style={{ fontSize: 13 }}>
            Units bought{" "}
            <input type="number" min={0} style={{ width: 100 }} value={quantity}
                   placeholder={String(d?.recommendation.quantity ?? "")}
                   onChange={(e) => setQuantity(e.target.value)} />
          </label>
          <button disabled={locked || busy}
                      onClick={() => onStep({ action: "record_buy", ...(quantity.trim() ? { quantity: Number(quantity) } : {}) })}>
            Record the buy
          </button>
        </div>
      )}

      {actions.includes("resolve") && (
        <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <label className="muted" style={{ fontSize: 13 }}>
            Follow-on{" "}
            <select value={resolution} onChange={(e) => setResolution(e.target.value)}>
              {FOLLOW_ON.map((r) => <option key={r} value={r}>{outcome(r)}</option>)}
            </select>
          </label>
          <button disabled={locked || busy}
                      onClick={() => onStep({ action: "resolve", resolution })}>Record follow-on</button>
        </div>
      )}

      {actions.includes("close") && (
        <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label className="muted" style={{ fontSize: 13 }}>
            Reason{" "}
            <input style={{ width: 260 }} value={reason} placeholder="why it ends with no follow-on"
                   onChange={(e) => setReason(e.target.value)} />
          </label>
          <button className="ghost" disabled={locked || busy || !reason.trim()}
                      onClick={() => onStep({ action: "close", reason: reason.trim() })}>
            Close without a follow-on
          </button>
        </div>
      )}

      </RoleGroup>

      {/* Outside the fieldset so the inputs table stays a focusable scroll region; each control
          below is disabled for anyone the group above gives the reason to. */}
      {canCreate && folded && (
        <div style={{ marginTop: 12 }}>
          <RoleButton role="engineer" me={me} className="ghost" disabled={busy} onClick={changeInputs}>
            Change inputs
          </RoleButton>
        </div>
      )}

      {canCreate && !folded && (
        <div style={{ marginTop: 16 }} className="stack">
          <h4 style={{ margin: 0 }}>{d && d.state === "proposed" ? "Change the inputs" : "Propose a last-time buy"}</h4>
          {options.length > 1 && (
            <select value={optionIndex} aria-label="Option to buy" disabled={locked} onChange={(e) => setPick(Number(e.target.value))}>
              {options.map((i) => (
                <option key={i} value={i}>{job.candidates?.[i]?.part_number ?? `option ${i + 1}`}</option>
              ))}
            </select>
          )}
          {chosen && (
            <div style={{ fontSize: 13 }}>
              <blockquote style={{ margin: "4px 0" }}>{chosen.quote ? `"${chosen.quote}"` : <span className="muted">no sentence quoted</span>}</blockquote>
              <SourceLink url={chosen.source_url} />
            </div>
          )}
          <TableScroll label="Last-time buy inputs">
          <table>
            <thead><tr><th>Input</th><th>Figure</th><th>Where it came from</th></tr></thead>
            <tbody>
              {LTB_INPUTS.map((i) => (
                <tr key={i.name}>
                  <td><label htmlFor={`ltb-${i.name}`}>{i.label}</label><div className="muted" style={{ fontSize: 11 }}>{i.hint}</div></td>
                  <td>
                    <input id={`ltb-${i.name}`} inputMode="decimal" style={{ width: 90 }} value={form[i.name].value} disabled={locked}
                           onChange={(e) => setForm({ ...form, [i.name]: { ...form[i.name], value: e.target.value } })} />
                  </td>
                  <td>
                    <input aria-label={`${i.label} source`} style={{ width: 220 }} placeholder="document, system or person" disabled={locked}
                           value={form[i.name].source}
                           onChange={(e) => setForm({ ...form, [i.name]: { ...form[i.name], source: e.target.value } })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableScroll>
          <div className="row" style={{ gap: 8 }}>
            <button disabled={locked || busy || optionIndex === undefined} onClick={workOut}>
              {busy ? "Working..." : "Work out the quantity"}
            </button>
            {d?.state === "proposed" && (
              <button type="button" className="ghost" disabled={busy} onClick={() => setChanging(null)}>Keep the proposal</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** The decision card with its call. Render with `key={job.id}` so a half-filled form stays with its job. */
export function LastTimeBuy({ job, me, onJob }: { job: UC5Job; me: Me | null | undefined; onJob: (j: UC5Job) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function step(body: LtbRequest) {
    setBusy(true); setError(null);
    try { onJob(await ltbStep(job.id, body)); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }
  return <LtbView job={job} me={me} busy={busy} error={error} onStep={step} />;
}
